import { useState, useEffect } from "react";
import { Link } from "wouter";
import { usePageTitle } from "@/hooks/use-page-title";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, User, Calendar, Activity, FileText, Receipt, CreditCard, Mail, MailX, Zap, Shield, Gift, Send, Check } from "lucide-react";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface UserDetail {
  id: number;
  name: string;
  email: string;
  plan: string;
  isAdmin: boolean;
  createdAt: string;
  lastActiveAt: string | null;
  quoteCount: number;
  invoiceCount: number;
  paidInvoiceCount: number;
  emailsSent: number;
  emailGateHits: number;
  stripeConnectStatus: string | null;
  stripeConnectAccountId: string | null;
  subscriptionStatus: string | null;
  events: Array<{ id: number; event: string; createdAt: string }>;
}

interface ReferralRecord {
  type: "signup" | "invite";
  id: number;
  email: string | null;
  name: string | null;
  status: string;
  createdAt: string;
  rewardedAt: string | null;
  referralCodeUsed: string | null;
}

interface AdminReferrals {
  referralCode: string | null;
  totalInvitesSent: number;
  rewardedCount: number;
  records: ReferralRecord[];
}

function fmtDate(iso: string | null): string {
  if (!iso) return "Never";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtDateShort(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function StatCard({ label, value, icon: Icon, color }: { label: string; value: string | number; icon: React.ElementType; color: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "0.875rem", padding: "0.875rem", border: "1px solid hsl(var(--border))", borderRadius: "0.75rem" }}>
      <div style={{ width: "2.25rem", height: "2.25rem", borderRadius: "0.5rem", background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon style={{ width: "1.125rem", height: "1.125rem", color }} />
      </div>
      <div>
        <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>{label}</div>
        <div style={{ fontSize: "1.125rem", fontWeight: 700 }}>{typeof value === "number" ? value.toLocaleString() : value}</div>
      </div>
    </div>
  );
}

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{ background: `${color}20`, color, padding: "0.2rem 0.625rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600 }}>
      {label}
    </span>
  );
}

function eventLabel(event: string): string {
  switch (event) {
    case "email_sent": return "Sent email";
    case "email_gate_hit": return "Hit email gate (no Pro plan)";
    default: return event.replace(/_/g, " ");
  }
}

function eventColor(event: string): string {
  switch (event) {
    case "email_sent": return "#3b82f6";
    case "email_gate_hit": return "#ef4444";
    default: return "#8b5cf6";
  }
}

function statusBadge(status: string) {
  if (status === "rewarded") {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", background: "hsl(142 76% 36% / 0.1)", color: "hsl(142 76% 36%)", border: "1px solid hsl(142 76% 36% / 0.2)", padding: "0.2rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600 }}>
        <Check style={{ width: "0.75rem", height: "0.75rem" }} />
        Rewarded
      </span>
    );
  }
  if (status === "invited") {
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: "0.25rem", background: "#3b82f618", color: "#3b82f6", border: "1px solid #3b82f630", padding: "0.2rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600 }}>
        <Send style={{ width: "0.75rem", height: "0.75rem" }} />
        Invited
      </span>
    );
  }
  return (
    <span style={{ background: "#6b728018", color: "#6b7280", border: "1px solid #6b728030", padding: "0.2rem 0.5rem", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 600 }}>
      Pending
    </span>
  );
}

export default function AdminUserDetail() {
  const match = window.location.pathname.match(/\/admin\/users\/(\d+)/);
  const userId = match ? match[1] : undefined;

  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [referrals, setReferrals] = useState<AdminReferrals | null>(null);
  const [referralsLoading, setReferralsLoading] = useState(false);

  usePageTitle(user?.name ? `Admin – ${user.name}` : "Admin – User");

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    fetch(`${BASE}/api/admin/users/${userId}/detail`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) throw new Error("Failed to fetch user detail");
        return res.json();
      })
      .then((d) => { setUser(d); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, [userId]);

  useEffect(() => {
    if (!userId) return;
    setReferralsLoading(true);
    fetch(`${BASE}/api/admin/users/${userId}/referrals`, { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) return null;
        return res.json();
      })
      .then((d) => { if (d) setReferrals(d); })
      .catch(() => {})
      .finally(() => setReferralsLoading(false));
  }, [userId]);

  if (loading) {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "200px" }}>
        <div style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>Loading user…</div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div style={{ padding: "1rem", background: "hsl(var(--destructive) / 0.1)", borderRadius: "0.5rem", color: "hsl(var(--destructive))" }}>
        {error || "User not found"}
      </div>
    );
  }

  return (
    <div style={{ maxWidth: "900px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.5rem" }}>
        <Link href="/admin">
          <button style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", background: "none", border: "1px solid hsl(var(--border))", borderRadius: "0.5rem", padding: "0.375rem 0.75rem", cursor: "pointer" }}>
            <ArrowLeft style={{ width: "0.875rem", height: "0.875rem" }} />
            Analytics
          </button>
        </Link>
        <Link href="/admin/users">
          <button style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.875rem", color: "hsl(var(--muted-foreground))", background: "none", border: "1px solid hsl(var(--border))", borderRadius: "0.5rem", padding: "0.375rem 0.75rem", cursor: "pointer" }}>
            User Management
          </button>
        </Link>
      </div>

      <Card style={{ marginBottom: "1.5rem" }}>
        <CardContent style={{ paddingTop: "1.5rem" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: "1rem" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <div style={{ width: "3.5rem", height: "3.5rem", borderRadius: "50%", background: "#3b82f620", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <User style={{ width: "1.75rem", height: "1.75rem", color: "#3b82f6" }} />
              </div>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                  <h1 style={{ fontSize: "1.375rem", fontWeight: 700 }}>{user.name}</h1>
                  <Badge label={user.plan === "pro" ? "Pro" : "Free"} color={user.plan === "pro" ? "#8b5cf6" : "#6b7280"} />
                  {user.isAdmin && <Badge label="Admin" color="#ef4444" />}
                </div>
                <p style={{ fontSize: "0.9375rem", color: "hsl(var(--muted-foreground))" }}>{user.email}</p>
              </div>
            </div>

            <div style={{ display: "flex", gap: "0.625rem", flexWrap: "wrap" }}>
              <Link href={`/admin/users`}>
                <button style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8125rem", background: "hsl(var(--secondary))", border: "1px solid hsl(var(--border))", borderRadius: "0.5rem", padding: "0.5rem 0.875rem", cursor: "pointer" }}>
                  <Shield style={{ width: "0.875rem", height: "0.875rem" }} />
                  Manage in Users
                </button>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem", marginBottom: "1.5rem" }}>
        <Card>
          <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>Account Info</CardTitle></CardHeader>
          <CardContent>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
              {[
                { label: "Signed up", value: fmtDate(user.createdAt), icon: Calendar, color: "#3b82f6" },
                { label: "Last active", value: fmtDate(user.lastActiveAt), icon: Activity, color: "#10b981" },
                { label: "Subscription", value: user.subscriptionStatus ?? (user.plan === "pro" ? "active" : "none"), icon: CreditCard, color: "#8b5cf6" },
                { label: "Stripe Connect", value: user.stripeConnectStatus ?? "Not connected", icon: Zap, color: "#f59e0b" },
              ].map((item) => (
                <div key={item.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.875rem", padding: "0.375rem 0", borderBottom: "1px solid hsl(var(--border))" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "hsl(var(--muted-foreground))" }}>
                    <item.icon style={{ width: "0.875rem", height: "0.875rem", color: item.color }} />
                    {item.label}
                  </div>
                  <span style={{ fontWeight: 500 }}>{item.value}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>Activity Stats</CardTitle></CardHeader>
          <CardContent>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
              <StatCard label="Quotes" value={user.quoteCount} icon={FileText} color="#3b82f6" />
              <StatCard label="Invoices" value={user.invoiceCount} icon={Receipt} color="#8b5cf6" />
              <StatCard label="Paid Invoices" value={user.paidInvoiceCount} icon={CreditCard} color="#10b981" />
              <StatCard label="Emails Sent" value={user.emailsSent} icon={Mail} color="#06b6d4" />
              <StatCard label="Email Gate Hits" value={user.emailGateHits} icon={MailX} color="#ef4444" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card style={{ marginBottom: "1.5rem" }}>
        <CardHeader>
          <CardTitle style={{ fontSize: "0.9375rem", display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <Gift style={{ width: "1rem", height: "1rem", color: "#8b5cf6" }} />
            Referrals
          </CardTitle>
        </CardHeader>
        <CardContent>
          {referralsLoading ? (
            <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>Loading referrals…</p>
          ) : referrals ? (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "0.75rem", marginBottom: "1.25rem" }}>
                <div style={{ padding: "0.75rem", border: "1px solid hsl(var(--border))", borderRadius: "0.75rem" }}>
                  <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", marginBottom: "0.25rem" }}>Current Code</div>
                  <div style={{ fontFamily: "monospace", fontSize: "0.875rem", fontWeight: 600 }}>{referrals.referralCode ?? "None"}</div>
                </div>
                <StatCard label="Invites Sent" value={referrals.totalInvitesSent} icon={Send} color="#3b82f6" />
                <StatCard label="Rewarded" value={referrals.rewardedCount} icon={Gift} color="#10b981" />
              </div>
              {referrals.records.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
                    <thead>
                      <tr style={{ borderBottom: "1px solid hsl(var(--border))" }}>
                        <th style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontWeight: 500, color: "hsl(var(--muted-foreground))", fontSize: "0.75rem", textTransform: "uppercase" }}>Type</th>
                        <th style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontWeight: 500, color: "hsl(var(--muted-foreground))", fontSize: "0.75rem", textTransform: "uppercase" }}>Contact</th>
                        <th style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontWeight: 500, color: "hsl(var(--muted-foreground))", fontSize: "0.75rem", textTransform: "uppercase" }}>Date</th>
                        <th style={{ textAlign: "left", padding: "0.5rem 0.75rem", fontWeight: 500, color: "hsl(var(--muted-foreground))", fontSize: "0.75rem", textTransform: "uppercase" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {referrals.records.map((r, i) => (
                        <tr key={`${r.type}-${r.id}`} style={{ borderBottom: i < referrals.records.length - 1 ? "1px solid hsl(var(--border) / 0.5)" : "none" }}>
                          <td style={{ padding: "0.625rem 0.75rem" }}>
                            <span style={{ fontSize: "0.75rem", fontWeight: 500, color: r.type === "invite" ? "#3b82f6" : "#8b5cf6" }}>
                              {r.type === "invite" ? "Invite" : "Sign-up"}
                            </span>
                          </td>
                          <td style={{ padding: "0.625rem 0.75rem", color: "hsl(var(--foreground))" }}>
                            <div>{r.name ?? <span style={{ color: "hsl(var(--muted-foreground))" }}>—</span>}</div>
                            {r.email && <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", fontFamily: "monospace" }}>{r.email}</div>}
                          </td>
                          <td style={{ padding: "0.625rem 0.75rem", color: "hsl(var(--muted-foreground))", fontSize: "0.8125rem" }}>{fmtDateShort(r.createdAt)}</td>
                          <td style={{ padding: "0.625rem 0.75rem" }}>{statusBadge(r.status)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>No invite or referral records yet.</p>
              )}
            </>
          ) : (
            <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>Could not load referral data.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle style={{ fontSize: "0.9375rem" }}>Activity Timeline</CardTitle></CardHeader>
        <CardContent>
          {user.events.length === 0 ? (
            <p style={{ fontSize: "0.875rem", color: "hsl(var(--muted-foreground))" }}>No recorded events yet.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
              {[...user.events].reverse().map((evt, i) => (
                <div key={evt.id} style={{ display: "flex", gap: "1rem", paddingBottom: "1rem", position: "relative" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div style={{ width: "0.75rem", height: "0.75rem", borderRadius: "50%", background: eventColor(evt.event), flexShrink: 0, marginTop: "0.25rem" }} />
                    {i < user.events.length - 1 && (
                      <div style={{ width: "1px", flex: 1, background: "hsl(var(--border))", marginTop: "0.25rem" }} />
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: "0.875rem", fontWeight: 500 }}>{eventLabel(evt.event)}</div>
                    <div style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))" }}>{fmtDate(evt.createdAt)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
